// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { buildMapCoordinatesKey, GetMapCoordinatesThunk } from './projectListThunk';
import { selectEffectiveOrderSortFilter } from './projectListSelector';

// Keeps the dashboard's project set in sync with the current search and filter. One fetch feeds
// all three surfaces - the grid, the map's pins, and the map rail's cards - so switching between
// grid and map view moves no data, and the set is complete rather than however many pages a user
// happened to scroll through.
//
// Search and filter are the server's to apply, and each answered set is cached under its key, so
// returning to a search already made costs nothing. Sort is not in the key: the set is complete,
// so ordering it is a client-side comparison rather than a request. A mutation the dashboard
// cannot apply in place (a folder deleted, a project moved from its folder page) clears the key
// instead, and that clears it here: the set is refetched while the dashboard is mounted, or
// on its next mount.
const useDashboardProjects = () => {
  const dispatch = useAppDispatch();
  const mapCoordinatesLoading = useAppSelector((state) => state.projectList.mapCoordinatesLoading);
  const keywordFilter = useAppSelector((state) => state.projectList.keywordFilter);
  // The effective filter, so the refetch fires exactly when the value the thunk sends changes.
  const orderSortFilter = useAppSelector(selectEffectiveOrderSortFilter);
  const mapCoordinatesKey = useAppSelector((state) => state.projectList.mapCoordinatesKey);
  // The key already dispatched this session, so back-to-back effect runs (a view flip plus a
  // debounce flush landing in the same commit) send one request, not two.
  const requestedKeyRef = useRef('');

  useEffect(() => {
    const key = buildMapCoordinatesKey(keywordFilter, orderSortFilter);
    // The store already answers this exact keyword|filter - ride the cached set instead of
    // re-running the aggregation.
    if (key === mapCoordinatesKey) return;
    if (key === requestedKeyRef.current && mapCoordinatesLoading) return;
    requestedKeyRef.current = key;
    dispatch(GetMapCoordinatesThunk());
  }, [keywordFilter, orderSortFilter, mapCoordinatesKey]);
};

export default useDashboardProjects;
